import groupBy from 'lodash/groupBy'
import { CommandType } from '~/constants/commands'
import { FlatIndex, useIndexesContext } from '~/contexts/IndexesContext'
import { getTaskRequest, useTask } from '~/contexts/TaskContext'
import { CodeContext } from '~/protos/agents'
import { IndexStatus } from '~/types/Indexes'

// Re-export for backward compatibility
export type { FlatIndex }

interface UseIndexProps {
  exculdeInternalRepos?: boolean
  commandType?: CommandType
}

export function useIndexes({
  exculdeInternalRepos = false,
  commandType = CommandType.UNRECOGNIZED
}: UseIndexProps = {}) {
  const { indexes: allIndexes } = useIndexesContext()

  const filteredIndexes = allIndexes.filter(index => {
    if (exculdeInternalRepos) {
      return index.indexer_metadata?.is_internal_uploaded_index === false
    }
    return true
  })

  // Code Review agent is not supported for local sync indexes
  if (commandType && [CommandType.CODE_REVIEW].includes(commandType)) {
    return filteredIndexes.filter(index => index.conn.conn_type !== 'local_sync')
  }

  return filteredIndexes
}

export function useFinishedIndexes({
  exculdeInternalRepos = false,
  commandType = CommandType.UNRECOGNIZED
}: UseIndexProps = {}) {
  const indexes = useIndexes({ exculdeInternalRepos, commandType })
  const successIndexes = indexes.filter(index => index.status === IndexStatus.SUCCEEDED)
  const branches = branchesByConnTypeAndIndexName(successIndexes)

  return {
    branches,
    repos: getReposFromBranches(branches)
  } as const
}

function branchesByConnTypeAndIndexName(
  indexes: FlatIndex[]
): Record<string, Record<string, string[]>> {
  const indexesByConnType = groupBy(indexes, 'conn.conn_type')
  return Object.fromEntries(
    Object.entries(indexesByConnType).map(([connType, connIndexes]) => {
      const repos = Object.fromEntries(
        Object.entries(groupBy(connIndexes, 'index_name')).map(([repoName, repoIndexes]) => {
          return [repoName, repoIndexes.map(index => index.index_branch)]
        })
      )
      return [connType, repos]
    })
  )
}

function getReposFromBranches(
  branches: Record<string, Record<string, string[]>>
): { index_name: string; conn_type: string }[] {
  return Object.entries(branches).flatMap(([conn_type, repos]) =>
    Object.keys(repos).map(index_name => ({ conn_type, index_name }))
  )
}

export function checkSameRepo(codeContext: CodeContext, index: FlatIndex) {
  const orgName = codeContext?.orgName
  const repoName = codeContext?.repoName
  const branchName = codeContext?.branchName
  return index.name.name === `${orgName}/${repoName}` && index.name.branch === branchName
}

export function checkIsInternalUploadedIndex(codeContext: CodeContext, indexes: FlatIndex[]) {
  const index = indexes.find(index => checkSameRepo(codeContext, index))

  if (index != null) {
    return index.indexer_metadata?.is_internal_uploaded_index ?? false
  }
  return false
}

export function checkIsLocalSyncIndex(codeContext: CodeContext, indexes: FlatIndex[]) {
  const index = indexes.find(index => checkSameRepo(codeContext, index))

  if (index != null) {
    return index.conn.conn_type === 'local_sync'
  }
  return false
}

export function useIsInternalUploadedIndex() {
  const indexes = useIndexes()
  const { task } = useTask()
  const codeContext = getTaskRequest(task)?.metadata?.codeContext
  if (!codeContext) return false
  return checkIsInternalUploadedIndex(codeContext, indexes)
}

export function useIsLocalSyncIndex() {
  const indexes = useIndexes()
  const { task } = useTask()
  const codeContext = getTaskRequest(task)?.metadata?.codeContext
  if (!codeContext) return false
  return checkIsLocalSyncIndex(codeContext, indexes)
}
